import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Switch, FormControlLabel, Stack, Typography, Box,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { saveStatutoryConfig } from '../services/api';

function defaultsFromSchema(schema) {
  const out = {};
  (schema?.fields || []).forEach(f => {
    if (f.default !== undefined) out[f.name] = f.default;
    else if (f.type === 'boolean') out[f.name] = false;
    else out[f.name] = '';
  });
  return out;
}

export default function StatutoryConfigDialog({ open, onClose, companyId, component, existingConfig, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const [values, setValues]   = useState({});
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (!component) return;
    const base = defaultsFromSchema(component.form_schema_json);
    setValues({ ...base, ...(existingConfig?.configuration_json || {}) });
  }, [component, existingConfig, open]);

  if (!component) return null;
  const fields = component.form_schema_json?.fields || [];

  const setField = (name, val) => setValues(v => ({ ...v, [name]: val }));

  const handleSave = async () => {
    // Coerce numbers; validate required
    const missing = fields.filter(f => f.required && (values[f.name] === '' || values[f.name] === null || values[f.name] === undefined));
    if (missing.length) {
      enqueueSnackbar(`Required: ${missing.map(f => f.label).join(', ')}`, { variant: 'warning' });
      return;
    }
    const cleaned = {};
    for (const f of fields) {
      let v = values[f.name];
      if (f.type === 'number' && v !== '' && v !== null && v !== undefined) v = Number(v);
      cleaned[f.name] = v;
    }
    setSaving(true);
    try {
      await saveStatutoryConfig({
        company_id: companyId,
        component_code: component.code,
        configuration_json: cleaned,
      });
      enqueueSnackbar(`${component.name} saved`, { variant: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      enqueueSnackbar('Error: ' + (e.response?.data?.message || e.message), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography fontWeight={700} fontSize={17}>Configure {component.name}</Typography>
        <Typography fontSize={12} color="text.secondary">{component.description}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {fields.map(f => {
            if (f.type === 'boolean') {
              return (
                <FormControlLabel
                  key={f.name}
                  control={
                    <Switch
                      checked={!!values[f.name]}
                      onChange={e => setField(f.name, e.target.checked)}
                    />
                  }
                  label={f.label}
                />
              );
            }
            if (f.type === 'enum') {
              return (
                <TextField
                  key={f.name}
                  select
                  size="small"
                  label={f.label + (f.required ? ' *' : '')}
                  value={values[f.name] ?? ''}
                  onChange={e => setField(f.name, e.target.value)}
                  fullWidth
                >
                  {(f.options || []).map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              );
            }
            return (
              <TextField
                key={f.name}
                size="small"
                type={f.type === 'number' ? 'number' : 'text'}
                label={f.label + (f.required ? ' *' : '')}
                value={values[f.name] ?? ''}
                onChange={e => setField(f.name, e.target.value)}
                fullWidth
              />
            );
          })}
          {fields.length === 0 && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography fontSize={13} color="text.secondary">No configurable fields for this component.</Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
